import React, { useState } from "react";
import { Button, Card, Radio, Space, Typography } from "antd";
import {
	useTenantSettings,
	useUpdateTenantSettings,
} from "@/hooks/useTenantSettings";
import type { TenantSettings } from "@/types/tenant";

const { Text, Paragraph } = Typography;

type ImageSource = NonNullable<TenantSettings["image_source"]>;

const IMAGE_SOURCE_OPTIONS: {
	value: ImageSource;
	label: string;
	help: string;
}[] = [
	{
		value: "ai",
		label: "AI-generated images",
		help: "The pipeline generates images with AI (default).",
	},
	{
		value: "client_assets",
		label: "Client's own assets",
		help:
			"Use the client's own labeled image library; each section is matched to its best-fitting photo.",
	},
	{
		value: "hybrid",
		label: "Hybrid (client assets + AI)",
		help: "Use the client's own images where available; AI-generate the rest.",
	},
	{
		value: "stock",
		label: "Stock images",
		help: "Use licensed stock photos (for clients who want neither AI nor their own assets).",
	},
];

/**
 * Per-client image-source setting.
 *
 * Writes to the tenant_settings table (image_source / stock_image_provider)
 * via the tenant-settings API — the same store the
 * backend image pipeline reads when deciding where images come from. It has its
 * own load/save cycle, independent of the content-preferences form on this page,
 * because those settings live in a different store (the preferences JSON blob).
 */
export const ImageSourceSettings: React.FC<{ website: string | null }> = ({
	website,
}) => {
	const { data: settings, isLoading } = useTenantSettings(website);
	const updateMutation = useUpdateTenantSettings();

	const [imageSource, setImageSource] = useState<ImageSource>("ai");
	const [stockProvider, setStockProvider] = useState<string>("");
	const [dirty, setDirty] = useState(false);

	// Hydrate local state from the loaded settings once per website, using
	// React's render-phase state-adjustment pattern (preferred over a
	// setState-in-effect). Re-hydrates when the selected tenant changes; a
	// refetch for the SAME tenant (e.g. after saving) does not clobber edits.
	const [hydratedFor, setHydratedFor] = useState<string | null>(null);
	if (settings && hydratedFor !== website) {
		setHydratedFor(website ?? null);
		setImageSource((settings.image_source as ImageSource) || "ai");
		setStockProvider(settings.stock_image_provider || "");
		setDirty(false);
	}

	const handleSave = async () => {
		if (!website) return;
		await updateMutation.mutateAsync({
			website,
			data: {
				image_source: imageSource,
				stock_image_provider: stockProvider || undefined,
			},
		});
		setDirty(false);
	};

	return (
		<Card size="small" style={{ marginBottom: 24 }} loading={isLoading}>
			<Text strong style={{ display: "block", marginBottom: 4 }}>
				Image Source
			</Text>
			<Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
				Choose where this client's article images come from.
			</Paragraph>

			<Radio.Group
				value={imageSource}
				onChange={(e) => {
					setImageSource(e.target.value);
					setDirty(true);
				}}
			>
				<Space direction="vertical">
					{IMAGE_SOURCE_OPTIONS.map((opt) => (
						<Radio key={opt.value} value={opt.value}>
							{opt.label}
							<Text
								type="secondary"
								style={{ fontSize: 12, display: "block", marginLeft: 0 }}
							>
								{opt.help}
							</Text>
						</Radio>
					))}
				</Space>
			</Radio.Group>

			{imageSource === "stock" && (
				<div style={{ marginTop: 16 }}>
					<Text style={{ display: "block", marginBottom: 4 }}>
						Stock provider
					</Text>
					<Radio.Group
						value={stockProvider || "unsplash"}
						onChange={(e) => {
							setStockProvider(e.target.value);
							setDirty(true);
						}}
					>
						<Radio value="unsplash">Unsplash</Radio>
						<Radio value="pexels">Pexels</Radio>
					</Radio.Group>
				</div>
			)}

			<div style={{ marginTop: 16 }}>
				<Button
					type="primary"
					size="small"
					disabled={!dirty || !website}
					loading={updateMutation.isPending}
					onClick={handleSave}
				>
					Save image source
				</Button>
			</div>
		</Card>
	);
};

export default ImageSourceSettings;
